import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { Link, useSearch } from "@tanstack/react-router";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "@/modules/reports/presentation/ui/ReportSummaryStatCard";
import {
  BriefcaseIcon,
  DownloadIcon,
  ExternalLinkIcon,
  UserCheckIcon,
  UserXIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { usePerformancePerformerReportQuery } from "../api/queries";
import {
  PerformanceReportDateFilter,
  type PerformanceReportSearch,
  usePerformanceReportDates,
} from "../ui/PerformanceReportDateFilter";
import {
  escapeCsvCell,
  formatPerformanceMoney,
  PerformanceReportsNav,
} from "../ui/PerformanceReportsNav";

export function PerformancePerformerReportPage() {
  const search = useSearch({
    from: "/app/performance/reports/performers",
  }) as PerformanceReportSearch;

  const { dateFrom, dateTo } = usePerformanceReportDates(search);
  const offset = search.offset ?? 0;
  const limit = 20;

  const query = useMemo(
    () => ({ dateFrom, dateTo, offset, limit }),
    [dateFrom, dateTo, offset],
  );

  const { data, isLoading } = usePerformancePerformerReportQuery(query);
  const performers = data?.performers ?? [];
  const summary = data?.summary;
  const total = data?.total ?? 0;

  const exportCsv = useCallback(() => {
    const header = "ນັກສະແດງ,ສະຖານະ,ຈຳນວນມອບໝາຍ,ຈຳນວນງານ,ຈ່າຍແລ້ວ,ລໍຈ່າຍ";
    const csvRows = performers.map((row) =>
      [
        escapeCsvCell(row.performerName),
        escapeCsvCell(row.status),
        escapeCsvCell(row.assignmentCount),
        escapeCsvCell(row.eventCount),
        escapeCsvCell(row.payoutPaid),
        escapeCsvCell(row.payoutCommitted),
      ].join(","),
    );
    const csv = [header, ...csvRows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `performance-performers-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [performers, dateFrom, dateTo]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            { label: "ລາຍງານງານສະແດງ", to: "/app/performance/reports" },
            { label: "ນັກສະແດງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານການໃຊ້ງານນັກສະແດງ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ການມອບໝາຍງານ, ການຈ່າຍເງິນ ແລະ ການໃຊ້ງານຕາມທີມ
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={performers.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <PerformanceReportsNav />

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <PerformanceReportDateFilter routeFrom="/app/performance/reports/performers" />
        </div>

        <ReportSummaryStatGrid columns={3}>
          <ReportSummaryStatCard
            title="ນັກສະແດງມີງານ"
            icon={UserCheckIcon}
            tone="revenue"
            isLoading={isLoading}
            value={reportSummaryCount(summary?.activeWithAssignments ?? 0)}
          />
          <ReportSummaryStatCard
            title="ນັກສະແດງບໍ່ມີງານ"
            icon={UserXIcon}
            tone="refunds"
            isLoading={isLoading}
            value={reportSummaryCount(summary?.idlePerformers ?? 0)}
          />
          <ReportSummaryStatCard
            title="ມອບໝາຍລວມ"
            icon={BriefcaseIcon}
            tone="attendance"
            isLoading={isLoading}
            value={reportSummaryCount(summary?.totalAssignments ?? 0)}
          />
        </ReportSummaryStatGrid>

        <Card>
          <CardHeader>
            <CardTitle>ນັກສະແດງ ({total})</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isLoading ? (
              <p className="text-muted-foreground text-sm">ກຳລັງໂຫລດ...</p>
            ) : performers.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ບໍ່ມີຂໍ້ມູນໃນຊ່ວງວັນທີທີ່ເລືອກ
              </p>
            ) : (
              <table className="w-full min-w-[860px] text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pr-4 pb-2 font-medium">ນັກສະແດງ</th>
                    <th className="pr-4 pb-2 font-medium">ສະຖານະ</th>
                    <th className="pr-4 pb-2 text-right font-medium">ມອບໝາຍ</th>
                    <th className="pr-4 pb-2 text-right font-medium">ງານ</th>
                    <th className="pr-4 pb-2 text-right font-medium">ຈ່າຍແລ້ວ</th>
                    <th className="pb-2 text-right font-medium">ລໍຈ່າຍ</th>
                  </tr>
                </thead>
                <tbody>
                  {performers.map((row) => (
                    <tr
                      key={row.performerId}
                      className="border-b last:border-0"
                    >
                      <td className="py-3 pr-4">
                        <Link
                          to="/app/performance/performers/$id"
                          params={{ id: row.performerId }}
                          className="inline-flex items-center gap-1 font-medium hover:underline"
                        >
                          {row.performerName}
                          <ExternalLinkIcon className="size-3" />
                        </Link>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline">
                          {row.status === "active" ? "ໃຊ້ງານ" : "ປິດ"}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-right">
                        {row.assignmentCount}
                      </td>
                      <td className="py-3 pr-4 text-right">{row.eventCount}</td>
                      <td className="whitespace-nowrap py-3 pr-4 text-right">
                        {formatPerformanceMoney(row.payoutPaid)}
                      </td>
                      <td className="whitespace-nowrap py-3 text-right">
                        {formatPerformanceMoney(row.payoutCommitted)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
